import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  InputNumber,
  notification,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import { actions, hooks, selectors } from 'farmx-redux-core';
import { updateCropDetails } from '../../../helper/mapHelper';
import { PageHeader } from '../components/PageHeader';
import {
  buildCropDetailsPayload,
  convertSpacingOnUnitChange,
  CROP_OPTIONS,
  getCropDetailsFormValues,
  IRRIGATION_TYPE_OPTIONS,
  SOIL_TYPE_OPTIONS,
} from './cropDetailsForm';
import './home.css';

const {
  selectAllNewBlocks,
  selectSensorStatus,
  selectSensorsForBlockId,
} = selectors;

const { updateNewBlockItem } = actions;

export function CropDetailsPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { selectedObjFromState } = hooks.useRanchBlockSelection();
  const getUserUnits = hooks.useUnits();
  const allBlocks = useSelector(selectAllNewBlocks);
  const selectedBlockId = selectedObjFromState?.type === 'block'
    ? Number(selectedObjFromState.value)
    : null;
  const selectedBlock = allBlocks.find((block) => block.id === selectedBlockId);
  const defaultBlock = selectedBlock || (allBlocks.length === 1 ? allBlocks[0] : null);
  const [editingBlockId, setEditingBlockId] = useState(defaultBlock?.id || null);
  const [hasInitializedForm, setHasInitializedForm] = useState(Boolean(defaultBlock));
  const [isSaving, setIsSaving] = useState(false);
  const [cropDetailsForm] = Form.useForm();
  const recommendationsEnabled = Form.useWatch('schedulingEnabled', cropDetailsForm);
  const initialValues = getCropDetailsFormValues(defaultBlock, getUserUnits);
  const isWaitingForBlockSelection = allBlocks.length > 1 && !editingBlockId;

  const soilSensorState = useSelector((state) => {
    if (!editingBlockId) return null;

    const soilSensors = selectSensorsForBlockId(state, editingBlockId, null, 'installed')
      .filter((sensor) => sensor?.type?.includes('soil'));
    if (!soilSensors.length) return 'missing';

    const hasOnlineSoilSensor = soilSensors.some((sensor) => (
      selectSensorStatus(state, sensor.type, sensor.identifier)?.online === true
    ));
    return hasOnlineSoilSensor ? 'online' : 'offline';
  });

  const selectBlock = useCallback((blockId) => {
    const block = allBlocks.find((item) => item.id === blockId);
    setEditingBlockId(blockId);
    setHasInitializedForm(true);
    cropDetailsForm.resetFields();
    cropDetailsForm.setFieldsValue(getCropDetailsFormValues(block, getUserUnits));
  }, [allBlocks, cropDetailsForm, getUserUnits]);

  useEffect(() => {
    if (!hasInitializedForm && defaultBlock) {
      setEditingBlockId(defaultBlock.id);
      cropDetailsForm.setFieldsValue(getCropDetailsFormValues(defaultBlock, getUserUnits));
      setHasInitializedForm(true);
    }
  }, [
    cropDetailsForm,
    defaultBlock,
    getUserUnits,
    hasInitializedForm,
  ]);

  const changeSpacingUnit = useCallback((fieldName, unitFieldName, nextUnit) => {
    const currentValue = cropDetailsForm.getFieldValue(fieldName);
    if (currentValue === undefined || currentValue === null || currentValue === '') {
      cropDetailsForm.setFieldsValue({ [unitFieldName]: nextUnit });
      return;
    }
    const convertedValue = convertSpacingOnUnitChange(
      currentValue,
      nextUnit,
      getUserUnits,
      5,
    );
    cropDetailsForm.setFieldsValue({
      [fieldName]: convertedValue,
      [unitFieldName]: nextUnit,
    });
  }, [cropDetailsForm, getUserUnits]);

  const saveCropDetails = useCallback(async (formValues) => {
    if (!editingBlockId || isSaving) return;

    setIsSaving(true);
    try {
      const cropDetailsPayload = buildCropDetailsPayload(formValues, getUserUnits);
      await updateCropDetails({
        blockId: editingBlockId,
        ...cropDetailsPayload,
      });
      dispatch(updateNewBlockItem({
        id: editingBlockId,
        changes: cropDetailsPayload,
      }));
      notification.success({
        message: t('Crop details saved successfully'),
      });
      history.push('/recommendation');
    } catch (error) {
      notification.error({
        message: t('Failed to save crop details'),
      });
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, editingBlockId, getUserUnits, history, isSaving, t]);

  let sensorMessage = null;
  if (soilSensorState === 'missing') {
    sensorMessage = t(
      'Irrigation recommendations need a soil sensor in this block. You can save anyway, but recommendations may not appear until one is installed.',
    );
  } else if (soilSensorState === 'offline') {
    sensorMessage = t(
      'Irrigation recommendations need an online soil sensor in this block. You can save anyway, but recommendations may not appear until one is online.',
    );
  }

  const nonNegativeNumberRules = [
    {
      type: 'number',
      min: 0,
      message: t('Enter zero or a positive number'),
    },
  ];
  const spacingUnitOptions = [
    { value: 'feet', label: t('Feet') },
    { value: 'meters', label: t('Meters') },
  ];

  const renderHelpLabel = (label, helpText) => (
    <span className="crop-details-label">
      <span>{t(label)}</span>
      <Tooltip
        trigger="click"
        placement="top"
        title={<span className="crop-details-help-text">{t(helpText)}</span>}
      >
        <button
          type="button"
          className="crop-details-help-button"
          aria-label={t(`Help for ${label}`)}
        >
          <InfoCircleOutlined />
        </button>
      </Tooltip>
    </span>
  );

  const renderSpacingField = (label, helpText, fieldName, unitFieldName) => (
    <div className="crop-details-spacing-field">
      {renderHelpLabel(label, helpText)}
      <div className="crop-details-spacing-input-row">
        <Form.Item
          name={fieldName}
          rules={nonNegativeNumberRules}
          className="crop-details-spacing-number"
        >
          <InputNumber
            min={0}
            step={0.01}
            precision={3}
            placeholder={t(`Enter ${label.toLowerCase()}`)}
            disabled={isWaitingForBlockSelection}
          />
        </Form.Item>
        <Form.Item name={unitFieldName}>
          <Select
            options={spacingUnitOptions}
            onChange={(unit) => changeSpacingUnit(fieldName, unitFieldName, unit)}
            disabled={isWaitingForBlockSelection}
          />
        </Form.Item>
      </div>
    </div>
  );

  return (
    <div className="crop-details-page page-content">
      <PageHeader
        showBack
        toPath="/recommendation"
        title={t('Edit block details')}
      />
      <Form
        form={cropDetailsForm}
        className="crop-details-form"
        layout="vertical"
        initialValues={initialValues}
        onFinish={saveCropDetails}
      >
        <Form.Item
          name="blockId"
          label={t('Block')}
          rules={[{ required: true, message: t('Select a block') }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('Select a block')}
            onChange={selectBlock}
            options={allBlocks.map((block) => ({
              value: block.id,
              label: block.name,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="crop"
          label={renderHelpLabel(
            'Crop Type',
            'Select the type of crop for this block.',
          )}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('Select a crop type')}
            disabled={isWaitingForBlockSelection}
            options={CROP_OPTIONS.map((option) => ({
              ...option,
              label: t(option.label),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="soilType"
          label={renderHelpLabel(
            'Soil Type',
            'Select the soil type for this block.',
          )}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('Select a soil type')}
            disabled={isWaitingForBlockSelection}
            options={SOIL_TYPE_OPTIONS.map((option) => ({
              ...option,
              label: t(option.label),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="datePlanted"
          label={renderHelpLabel(
            'Date Planted',
            'Select the date when the crop was planted.',
          )}
        >
          <DatePicker
            format="DD-MM-YYYY"
            placeholder={t('Select the date planted')}
            disabledDate={(date) => date && date.endOf('day').isAfter()}
            disabled={isWaitingForBlockSelection}
          />
        </Form.Item>
        {renderSpacingField(
          'Row Spacing',
          'Distance between rows of plants.\nThis helps calculate plant density.',
          'rowSpacing',
          'rowSpacingUnit',
        )}
        {renderSpacingField(
          'Tree Spacing',
          'Distance between individual plants in a row.\nThis helps calculate plant density.',
          'treeSpacing',
          'treeSpacingUnit',
        )}
        <Form.Item
          name="irrigationType"
          label={renderHelpLabel(
            'Irrigation Type',
            'Irrigation type of the block',
          )}
        >
          <Select
            placeholder={t('Select an irrigation type')}
            disabled={isWaitingForBlockSelection}
            options={IRRIGATION_TYPE_OPTIONS.map((option) => ({
              ...option,
              label: t(option.label),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="irrigationLine"
          label={renderHelpLabel(
            'Irrigation Line',
            'Number of drip line that distributes water to emitters',
          )}
          rules={[
            {
              type: 'integer',
              min: 1,
              message: t('Enter a whole number greater than zero'),
            },
          ]}
        >
          <InputNumber
            min={1}
            step={1}
            precision={0}
            placeholder={t('Enter irrigation lines')}
            disabled={isWaitingForBlockSelection}
          />
        </Form.Item>
        {renderSpacingField(
          'Emitter Spacing',
          'The distance between two emitters on a drip irrigation line',
          'emitterSpacing',
          'emitterSpacingUnit',
        )}
        <Form.Item
          name="emitterDischarge"
          label={renderHelpLabel(
            'Emitter Discharge (GPH)',
            'Value to calculate flow rate.\nEmitter Discharge = water released per hour',
          )}
          rules={nonNegativeNumberRules}
        >
          <InputNumber
            min={0}
            step={0.01}
            precision={3}
            placeholder={t('Enter emitter discharge')}
            disabled={isWaitingForBlockSelection}
          />
        </Form.Item>
        <Form.Item>
          <div className="enable-recommendations-form-row crop-details-recommendations-row">
            {renderHelpLabel(
              'Enable Recommendations',
              'Irrigation recommendations requires :\n• Irrigation Recommendation enabled\n• Growing season start\n• Date planted\n• Atleast one soil sensor installed in the current block',
            )}
            <Form.Item name="schedulingEnabled" valuePropName="checked" noStyle>
              <Switch disabled={isWaitingForBlockSelection} />
            </Form.Item>
          </div>
        </Form.Item>
        {recommendationsEnabled && sensorMessage && (
          <Alert
            banner
            showIcon
            type="info"
            message={sensorMessage}
            className="crop-details-sensor-alert"
          />
        )}
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={isSaving}
          disabled={isWaitingForBlockSelection}
          className="crop-details-save-button"
        >
          {sensorMessage && recommendationsEnabled ? t('Save Anyway') : t('Save')}
        </Button>
      </Form>
    </div>
  );
}
